import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import {
  SideBarModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule
} from "ag-grid-enterprise";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";

/* Register AG Grid modules */
ModuleRegistry.registerModules([
  AllCommunityModule,
  SideBarModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule
]);

/* ---------- Styles (inline for portability) ---------- */
const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  background: "rgba(0,0,0,0.55)",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  paddingTop: "30px",
  zIndex: 99999
};
const defaultVisibleFields = [
  "stateName",
  "districtName",
  "villageName",
  "farmerName",
  "seasonName",
  "cropName",
  "finYear",
  "createdDate"

];

const modalBoxStyle = {
  width: "95%",
  maxWidth: "1200px",
  maxHeight: "90vh",
  overflowY: "auto",
  background: "#fff",
  borderRadius: 10,
  boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
  padding: 18,
  position: "relative"
};

const closeBtnStyle = {
  position: "absolute",
  top: 12,
  right: 12,
  background: "#e74c3c",
  color: "#fff",
  border: "none",
  padding: "8px 10px",
  borderRadius: 6,
  cursor: "pointer"
};

const mapContainerStyle = { width: "100%", height: "360px" };

const skeletonStyle = {
  background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.4s infinite",
  borderRadius: 6,
  height: 20,
  width: "100%"
};

/* Inject keyframe animations once */
if (!document.getElementById("km-modal-animations")) {
  const style = document.createElement("style");
  style.id = "km-modal-animations";
  style.textContent = `
    @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
    @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  `;
  document.head.appendChild(style);
}

/* ---------- Component: modal that renders the full detail UI ---------- */
function FarmerDetailModal({ open, onClose, detail, isMapLoaded, isLoadingFull }) {
  const [map, setMap] = useState(null);

  const onLoad = useCallback(
    (mapInstance) => {
      setMap(mapInstance);

      // Draw polygons (if provided)
      if (!detail) return;
      const polys = detail.farmerCropSurveyPolygonList || [];
      const bounds = new window.google.maps.LatLngBounds();

      const polygons = [];
      const markers = [];

      polys.forEach((p) => {
        try {
          const parsed = JSON.parse(p.polygonShape);
          // create polygon
          const poly = new window.google.maps.Polygon({
            paths: parsed,
            strokeColor: "#00a65a",
            strokeOpacity: 0.9,
            strokeWeight: 2,
            fillColor: "#00a65a",
            fillOpacity: 0.12
          });
          poly.setMap(mapInstance);
          polygons.push(poly);

          // extend bounds
          parsed.forEach((latlng) => bounds.extend({ lat: latlng.lat || latlng[1] || latlng[0], lng: latlng.lng || latlng[0] || latlng[1] }));
          // compute center roughly
          const center = poly.getPath().getArray().reduce(
            (acc, latLng) => ({ lat: acc.lat + latLng.lat(), lng: acc.lng + latLng.lng() }),
            { lat: 0, lng: 0 }
          );
          if (poly.getPath().getLength() > 0) {
            const len = poly.getPath().getLength();
            const centroid = { lat: center.lat / len, lng: center.lng / len };
            const marker = new window.google.maps.Marker({
              position: centroid,
              map: mapInstance
            });
            markers.push(marker);
            marker.addListener("click", () => {
              mapInstance.panTo(marker.getPosition());
              mapInstance.setZoom(18);
            });
          }
        } catch (e) {
          console.warn("Failed to parse polygon:", e);
        }
      });

      if (!bounds.isEmpty()) {
        mapInstance.fitBounds(bounds);
      } else {
        // fallback center using detail lat/long if available
        const lat = detail.lat ?? detail.latitude ?? null;
        const lng = detail.long ?? detail.longitude ?? null;
        if (lat && lng) {
          mapInstance.setCenter({ lat: Number(lat), lng: Number(lng) });
          mapInstance.setZoom(18);
        }
      }
    },
    [detail]
  );

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  if (!open) return null;

  return (
    <div style={{ width: "100%" }}>
      <div style={modalBoxStyle}>

        {/* Loading banner — shown while full detail is being fetched */}
        {isLoadingFull && (
          <div style={{
            background: "linear-gradient(90deg, #e8f5e9, #f1f8e9, #e8f5e9)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite",
            borderRadius: 6,
            padding: "10px 16px",
            marginBottom: 12,
            color: "#2e7d32",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
            <span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⟳</span>
            Loading full details...
          </div>
        )}

        {/* Map */}
        <div style={{ marginBottom: 16 }}>
          {isMapLoaded ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={
                detail && (detail.lat || detail.latitude)
                  ? { lat: Number(detail.lat ?? detail.latitude), lng: Number(detail.long ?? detail.longitude) }
                  : { lat: 21.0, lng: 78.0 }
              }
              zoom={16}
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={{ mapTypeId: "satellite" }}
            />
          ) : (
            <div style={{ height: 360, display: "flex", alignItems: "center", justifyContent: "center", background: "#f0f4f0", borderRadius: 8 }}>
              Loading map...
            </div>
          )}
        </div>

        {/* Survey Details */}
        <h3 style={{ borderLeft: "4px solid #2ecc71", paddingLeft: 10 }}>Survey Details</h3>
        <div style={{ background: "#f7f9fb", padding: 16, marginTop: 12, borderRadius: 6 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700 }}>Scheme Name</div>
              <div>{detail?.schemeName ?? "-"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 700 }}>Farmer Name</div>
              <div>{detail?.farmerName ?? "-"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 700 }}>State</div>
              <div>{detail?.stateName ?? "-"}</div>
            </div>

            <div>
              <div style={{ fontWeight: 700 }}>District</div>
              <div>{detail?.districtName ?? "-"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 700 }}>Sub District</div>
              <div>{detail?.subDistrictName ?? "-"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 700 }}>Village</div>
              <div>{detail?.villageName ?? "-"}</div>
            </div>

            <div>
              <div style={{ fontWeight: 700 }}>Mobile No</div>
              <div>{detail?.mobileNo ?? "-"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 700 }}>Created Date</div>
              <div>{detail?.createdOn ? new Date(detail.createdOn).toLocaleDateString() : "-"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 700 }}>Aadhaar</div>
              <div>{detail?.aadharNo ?? "-"}</div>
            </div>
          </div>
        </div>

        {/* Aadhaar Images */}
        <h3 style={{ borderLeft: "4px solid #2ecc71", paddingLeft: 10, marginTop: 20 }}>Farmer Aadhaar Image</h3>
        <div style={{ display: "flex", gap: 12, paddingTop: 12 }}>
          {(detail?.farmerAadhaarImageList || []).length === 0 && <div>No Aadhaar Images</div>}
          {(detail?.farmerAadhaarImageList || []).map((img, idx) => (
            <img
              key={idx}
              src={img.attPath}
              alt={`aadhaar-${idx}`}
              style={{ width: 160, height: 100, objectFit: "cover", borderRadius: 8, border: "3px solid #2e8b57" }}
            />
          ))}
        </div>

        {/* Seed Distribution */}
        <h3 style={{ borderLeft: "4px solid #2ecc71", paddingLeft: 10, marginTop: 20 }}>Farmer Seed Distribution Image</h3>
        <div style={{ paddingTop: 12 }}>
          {(detail?.farmerSeedDistributionImageList || []).length === 0 && <div>No Seed Distribution Info</div>}
          {(detail?.farmerSeedDistributionImageList || []).map((sd, sidx) => (
            <div key={sidx} style={{ background: "#fafbfc", padding: 12, borderRadius: 6, marginBottom: 12, border: "1px solid #e9eef2" }}>
              <div style={{ marginBottom: 8 }}>
                <strong>Seed Quantity (KG):</strong> {sd.seedQuantityKG ?? sd.seedQuantity ?? "-"} <br />
                <strong>Seed Type:</strong> {sd.ctlsName ?? sd.seedType ?? "-"} <br />
                <strong>Distribution Date:</strong> {sd.seedDistributionDate ?? sd.distributionDate ?? "-"}
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                {(sd.seedDistributionImageLists || []).map((img, idx) => (
                  <img key={idx} src={img.attPath} alt={`seed-${idx}`} style={{ width: 160, height: 100, objectFit: "cover", borderRadius: 6 }} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Crop Survey Images */}
        <h3 style={{ borderLeft: "4px solid #2ecc71", paddingLeft: 10, marginTop: 20 }}>Farmer Crop Survey Image</h3>
        <div style={{ display: "flex", gap: 12, paddingTop: 12, flexWrap: "wrap" }}>
          {(detail?.farmerCropSurveyImageList || []).length === 0 && <div>No Crop Survey Images</div>}
          {(detail?.farmerCropSurveyImageList || []).map((img, idx) => (
            <img key={idx} src={`https://krishimapper.dac.gov.in/appdevapi/api/common/GetAttachbyURL?imageURL=${img.attPath}`} alt={`crop-${idx}`} style={{ width: 160, height: 100, objectFit: "cover", borderRadius: 6 }} />
          ))}
        </div>

        {/* optional sections could be added similarly (training, soil health etc.) */}
      </div>
    </div>
  );
}

/* ---------- Cell renderer button (React) ---------- */
const ViewButtonRenderer = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      style={{
        background: "transparent",
        border: "none",
        cursor: "pointer",
        fontSize: 18
      }}
      title="View Detail"
    >
      👁
    </button>
  );
};

/* ---------- React Query fetch function ---------- */
const fetchFarmerDetail = async ({ schemeCode, farmerId }) => {
  let apiurl = "";
  let payload = {};

  if (String(schemeCode) === "5") {
    apiurl = "/api/LatLongPolygonList/CompleteFarmerData_KM";
    payload = { SchemeCode: String(schemeCode), lngA_FarmerCode: farmerId };
  } else if (String(schemeCode) === "3") {
    apiurl = "/api/Nature/FarmerDetails/getProjectDetailsList";
    payload = { SchemeCode: String(schemeCode), projectCode: farmerId };
  } else if (String(schemeCode) === "8") {
    apiurl = "/api/MIDH/MIDHFarmerDetails/GetMidhFarmerDetailsAsync";
    payload = { SchemeCode: String(schemeCode), farmerCode: farmerId };
  } else if (String(schemeCode) === "12") {
    apiurl = "/api/GT/GetSurveyDetails";
    payload = { SchemeCode: String(schemeCode), cropSurveyCode: farmerId };
  } else {
    throw new Error("Scheme not supported yet");
  }

  const baseURL =
    process.env.NODE_ENV === "development"
      ? process.env.REACT_APP_DEV_MODE
      : process.env.REACT_APP_PRO_MODE;

  const res = await axios.post(`${baseURL}${apiurl}`, payload);

  if (
    res.data?.dataContent &&
    Array.isArray(res.data.dataContent) &&
    res.data.dataContent.length > 0
  ) {
    return res.data.dataContent[0];
  }
  return null;
};

/* ---------- Main component (AG Grid + modal) ---------- */
export default function SurveyDataTableViewWithDetailModal({ data }) {
  // Load Google Maps script once at the top level — not inside the modal
  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyD4X4kzlpReymgcXUXGh0Q3TalmxJmasM0",
  });

  const [gridApi, setGridApi] = useState(null);
  const [searchText, setSearchText] = useState("");

  // modal + selected farmer states
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFarmer, setSelectedFarmer] = useState(null); // { schemeCode, farmerId }
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedVillage, setSelectedVillage] = useState("");
  const [stateList, setStateList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [villageList, setVillageList] = useState([]);

  // React Query — fetches only when selectedFarmer is set; caches per farmer
  const {
    data: detailData,
    isFetching: loadingDetail,
    error: detailError
  } = useQuery({
    queryKey: ["farmerDetail", selectedFarmer?.schemeCode, selectedFarmer?.farmerId],
    queryFn: () => fetchFarmerDetail(selectedFarmer),
    enabled: !!selectedFarmer?.farmerId,
    // Show row data immediately while full detail loads in background
    placeholderData: selectedFarmer?.rowData ?? undefined,
    staleTime: 5 * 60 * 1000,   // 5 min — avoid re-fetch on re-open
    retry: 1
  });
  // Convert row keys to columns
  useEffect(() => {
    if (!data || data.length === 0) return;

    // unique states
    const states = [...new Set(data.map(d => d.stateName).filter(Boolean))];
    setStateList(states);

  }, [data]);
  const onStateChange = (e) => {
    const state = e.target.value;
    setSelectedState(state);
    setSelectedDistrict("");
    setSelectedVillage("");
    setVillageList([]);

    if (!state) {
      setDistrictList([]);
      applyGridFilter("", "");
      return;
    }

    const districts = [
      ...new Set(
        data
          .filter(d => d.stateName === state)
          .map(d => d.districtName)
          .filter(Boolean)
      )
    ];

    setDistrictList(districts);
    applyGridFilter(state, "");
  };
  const onDistrictChange = (e) => {
    const district = e.target.value;

    setSelectedDistrict(district);
    setSelectedVillage("");

    if (!district) {
      setVillageList([]);
      applyGridFilter(selectedState, "", "");
      return;
    }

    const villages = [
      ...new Set(
        data
          .filter(
            d =>
              d.stateName === selectedState &&
              d.districtName === district
          )
          .map(d => d.villageName)
          .filter(Boolean)
      )
    ];

    setVillageList(villages);
    applyGridFilter(selectedState, district, "");
  };
  const onVillageChange = (e) => {
    const village = e.target.value;
    setSelectedVillage(village);
    applyGridFilter(selectedState, selectedDistrict, village);
  };
  const applyGridFilter = (state, district, village) => {
    if (!gridApi) return;

    const filterModel = {};

    if (state) {
      filterModel.stateName = { type: "equals", filter: state };
    }
    if (district) {
      filterModel.districtName = { type: "equals", filter: district };
    }
    if (village) {
      filterModel.villageName = { type: "equals", filter: village };
    }

    gridApi.setFilterModel(filterModel);
  };




  const baseColumns = data && data.length > 0
    ? Object.keys(data[0]).map((key) => ({
      headerName: key.charAt(0).toUpperCase() + key.slice(1),
      field: key,
      filter: true,
      sortable: true,
      resizable: true,
      minWidth: 120
    }))
    : [];


  const columnDefs = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    return Object.keys(data[0]).map((key) => ({
      headerName: key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase()),
      field: key,
      hide: !defaultVisibleFields.includes(key), // 🔥 IMPORTANT
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 150
    }));
  }, [data]);
  const finalColumnDefs = React.useMemo(() => {
    return [
      ...columnDefs,
      {
        headerName: "View Detail",
        field: "viewDetail",
        pinned: "right",
        width: 110,
        cellRenderer: () => "👁",
        onCellClicked: (params) => {
          const schemeCode = params.data.schemeCode ?? params.data.schemecode ?? "5";
          const farmerId =
            params.data.farmerId ??
            params.data.farmerID ??
            params.data.farmerCodeId ??
            params.data.lngA_FarmerCode ??
            params.data.farmerCode;
          openDetailModal(schemeCode, farmerId, params.data);
        }
      }
    ];
  }, [columnDefs]);


  const schemeName = data?.[0]?.schemeName ?? null;
  const finYear = data?.[0]?.finYear ?? null;
  const onGridReady = (params) => setGridApi(params.api);

  const onSearch = (e) => {
    setSearchText(e.target.value);
    gridApi?.setQuickFilter(e.target.value);
  };

  const showColumnsPanel = () => gridApi?.openToolPanel("columns");
  const showFiltersPanel = () => gridApi?.openToolPanel("filters");
  const exportCSV = () => gridApi?.exportDataAsCsv();

  /* ---------- open modal and trigger React Query fetch ---------- */
  const openDetailModal = (schemeCode, farmerId, rowData) => {
    if (!farmerId) {
      alert("Farmer id/code missing on row!");
      return;
    }
    // rowData is the grid row — shown instantly as placeholder while API loads
    setSelectedFarmer({ schemeCode, farmerId, rowData });
    setModalOpen(true);
  };


  return (
    <>
      {/* HEADER */}
      <div className="total-survey-card filterschemebg">
        <div className="text-center">

          <h5 className="text-black">
            <b>{schemeName}</b>
          </h5>
          <h5 className="text-black">Total Surveys: {data.length}</h5>
          <p>({finYear})</p>
        </div>
      </div>
      <div style={styles.toolbarContainer}>
        {/* LEFT: Filters */}
        <div style={styles.leftSection}>
          <select value={selectedState} onChange={onStateChange} style={styles.select}>
            <option value="">All States</option>
            {stateList.map((s, i) => (
              <option key={i} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={selectedDistrict}
            onChange={onDistrictChange}
            style={styles.select}
            disabled={!selectedState}
          >
            <option value="">All Districts</option>
            {districtList.map((d, i) => (
              <option key={i} value={d}>{d}</option>
            ))}
          </select>

          <select
            value={selectedVillage}
            onChange={onVillageChange}
            style={styles.select}
            disabled={!selectedDistrict}
          >
            <option value="">All Villages</option>
            {villageList.map((v, i) => (
              <option key={i} value={v}>{v}</option>
            ))}
          </select>
        </div>

        {/* RIGHT: Buttons */}
        <div style={styles.rightSection}>
          <button className="puplebtn" onClick={showColumnsPanel}>
            📊 Columns
          </button>
          <button className="greenbtn" onClick={showFiltersPanel}>
            🔍 Filters
          </button>
          <button className="downloadbtn" onClick={exportCSV}>
            ⬇️ Download
          </button>
        </div>
      </div>

      {/* AG Grid */}
      <div >
        <div
          className="ag-theme-material "
          style={{
            fontSize: "17px",
            height: "75vh",
            width: "100%",
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
            border: "1px solid #e5e7eb",
            background: "#fff"
          }}
        >
          <AgGridReact
            rowData={data}

            columnDefs={finalColumnDefs}
            pagination={true}
            paginationPageSize={15}
            paginationPageSizeSelector={[10, 15, 25, 50, 100]}
            animateRows={true}
            rowHeight={44}
            suppressPaginationPanel={false}
            paginationAutoPageSize={false}
            overlayNoRowsTemplate={
              '<span style="padding:10px;font-size:14px;color:#555;">No records found</span>'
            }
            domLayout="normal"
            sideBar={{
              toolPanels: ["columns", "filters"],
              defaultToolPanel: ""
            }}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
              flex: 1,
              minWidth: 150,
              headerClass: "grid-header",
              cellClass: "grid-cell"
            }}
            onGridReady={onGridReady}
          />
        </div>
      </div>
      {/* Modal — render detail layout inside modal */}
      {modalOpen && (
        <div style={overlayStyle}>
          <div style={modalBoxStyle}>
            {/* close button */}
            <button style={closeBtnStyle} onClick={() => { setModalOpen(false); setSelectedFarmer(null); }}>
              Close
            </button>

            {/* Show data immediately from placeholder (row data), full detail loads in background */}
            {detailData && (
              <FarmerDetailModal
                open={true}
                onClose={() => { setModalOpen(false); setSelectedFarmer(null); }}
                detail={detailData}
                isMapLoaded={isMapLoaded}
                isLoadingFull={loadingDetail}
              />
            )}

            {/* No placeholder yet — show a minimal skeleton */}
            {!detailData && loadingDetail && (
              <div style={{ padding: 30 }}>
                <div style={skeletonStyle} />
                <div style={{ ...skeletonStyle, width: "60%", marginTop: 12 }} />
                <div style={{ ...skeletonStyle, width: "80%", marginTop: 12 }} />
                <div style={{ ...skeletonStyle, height: 200, marginTop: 20 }} />
              </div>
            )}

            {!detailData && !loadingDetail && detailError && (
              <div style={{ padding: 20 }}>
                <h4>Error loading details</h4>
                <p>{detailError?.message ?? "An unexpected error occurred."}</p>
              </div>
            )}
            {!detailData && !loadingDetail && !detailError && (
              <div style={{ padding: 20 }}>
                <h4>No details found</h4>
                <p>Either the API returned no data or the response couldn't be parsed.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
const styles = {
  toolbarContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px",
    background: "#FFF",
    borderRadius: 8,
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
    marginBottom: 10,
    gap: "10px"
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    padding: 12,
    background: "#fff",
    borderRadius: 8,
    marginBottom: 10
  },
  left: {
    display: "flex",
    gap: 10
  },
  select: {
    padding: "7px 12px",
    borderRadius: 6,
    border: "1px solid #ccc",
    minWidth: 180
  },
  search: {
    padding: "7px 14px",
    borderRadius: 6,
    border: "1px solid #ccc",
    width: 250
  },
  leftSection: { display: "flex", gap: 10, flexWrap: "wrap" },
  searchBox: {
    padding: "7px 14px",
    width: 250,
    borderRadius: 6,
    border: "1px solid #ccc"
  }
};